import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronRight, HandHeart } from 'lucide-react';
import { timeAgo } from '../../lib/notificationInbox';

// Inicio → Tu ciudad → Incidencias. It was three lines of small text: a label, one title and the
// counts (Tanuj, 28 Sep 2026: «it's plain text»). Now the counts are chips, and the latest reports
// are rows you can open, each with its category, barrio, age and support: what a neighbour needs
// to see whether their street is already reported.
const CATEGORY_ICON = {
  baches: '🕳️', alumbrado: '💡', limpieza: '🧹', mobiliario: '🪑', arbolado: '🌳', accesibilidad: '♿',
  ruido: '🔊', agua: '💧', trafico: '🚦', animales: '🐾', otros: '📌',
};

// How many rows: one when the card shares a phone's width with Ayuda vecinal, two from sm or at full
// width, three from lg (where it sits in its own column).
function rowClass(i, compact) {
  if (i === 0) return '';
  if (i === 1) return compact ? 'hidden sm:block' : '';
  return 'hidden lg:block';
}

export default function IncidenciasCard({ incidencias, pendientes, resueltas, compact = false, className = '' }) {
  const navigate = useNavigate();
  const items = (incidencias || []).slice(0, 3);
  if (items.length === 0) return null;
  return (
    <div className={`p-2.5 lg:p-3 rounded-xl bg-slate-50 border border-slate-100 min-w-0 ${className}`} data-testid="incidencia-banner">
      <button type="button" onClick={() => navigate('/incidencias')}
        className="w-full flex items-center justify-between gap-2 min-h-[36px] text-left group"
        data-testid="incidencia-banner-all">
        <span className="flex items-center gap-2 min-w-0">
          <span className="hidden min-[360px]:flex w-7 h-7 shrink-0 rounded-lg bg-red-50 border border-red-100 items-center justify-center" aria-hidden="true">
            <AlertTriangle size={14} className="text-red-500" />
          </span>
          <span className="text-sm font-extrabold text-torre-slate leading-tight break-words min-w-0">Incidencias</span>
        </span>
        <ChevronRight size={16} className="hidden sm:block shrink-0 text-slate-400 group-hover:text-torre-teal transition-colors" aria-hidden="true" />
      </button>

      <div className="flex flex-wrap gap-1.5 mt-1.5 mb-2" data-testid="incidencia-banner-counts">
        <span className="inline-flex items-center px-2 py-0.5 rounded-xl bg-amber-50 border border-amber-200 text-[11px] font-bold text-amber-700 tabular-nums">
          {/* Not «pendientes»: the dashboard counts every report not yet resolved, en revisión
              included, so it read 18 where /incidencias said 14 pendientes + 4 en revisión. */}
          {pendientes} sin resolver
        </span>
        {resueltas > 0 && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-xl bg-emerald-50 border border-emerald-200 text-[11px] font-bold text-emerald-700 tabular-nums">
            {resueltas} resuelta{resueltas !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      <ul className="space-y-0.5" aria-label="Últimas incidencias">
        {items.map((inc, i) => {
          const meta = [inc.barrio, timeAgo(inc.fecha), inc.votos > 0 ? `${inc.votos} apoyo${inc.votos !== 1 ? 's' : ''}` : null]
            .filter(Boolean).join(' · ');
          return (
            <li key={inc.id || i} className={rowClass(i, compact)}>
              <button type="button" onClick={() => navigate(inc.id ? `/incidencias/${inc.id}` : '/incidencias')}
                className="w-full flex items-start gap-2 text-left rounded-lg px-1.5 py-1.5 -mx-1.5 hover:bg-white transition-colors min-h-[44px]"
                data-testid={`incidencia-banner-item-${i + 1}`}>
                <span className="text-sm leading-5 shrink-0" aria-hidden="true">{CATEGORY_ICON[inc.categoria] || CATEGORY_ICON.otros}</span>
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-bold text-torre-slate line-clamp-2 break-words">{inc.titulo}</span>
                  {meta && <span className="block text-[11px] text-slate-500 line-clamp-2">{meta}</span>}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Its neighbour in Tu ciudad, in the same shape: the two used to differ, one plain text and one a
// coloured block, and side by side on a phone they did not read as a pair.
export function AyudaCard({ request, abiertas = 0, className = '' }) {
  const navigate = useNavigate();
  if (!request) return null;
  const meta = [request.barrio, timeAgo(request.fecha)].filter(Boolean).join(' · ');
  return (
    <div className={`p-2.5 lg:p-3 rounded-xl bg-amber-50/70 border border-amber-200 min-w-0 ${className}`} data-testid="help-request-banner">
      <button type="button" onClick={() => navigate('/vecinos')}
        className="w-full flex items-center justify-between gap-2 min-h-[36px] text-left group" data-testid="help-request-banner-all">
        <span className="flex items-center gap-2 min-w-0">
          <span className="hidden min-[360px]:flex w-7 h-7 shrink-0 rounded-lg bg-white border border-amber-200 items-center justify-center" aria-hidden="true">
            <HandHeart size={14} className="text-amber-600" />
          </span>
          <span className="text-sm font-extrabold text-torre-slate leading-tight break-words min-w-0">Ayuda vecinal</span>
        </span>
        <ChevronRight size={16} className="hidden sm:block shrink-0 text-slate-400 group-hover:text-torre-teal transition-colors" aria-hidden="true" />
      </button>
      <div className="flex flex-wrap gap-1.5 mt-1.5 mb-2">
        <span className="inline-flex items-center px-2 py-0.5 rounded-xl bg-white border border-amber-200 text-[11px] font-bold text-amber-700 tabular-nums" data-testid="help-request-banner-count">{abiertas > 1 ? `${abiertas} abiertas` : 'Abierta'}</span>
      </div>
      <button type="button" onClick={() => navigate(request.id ? `/vecinos/${request.id}` : '/vecinos')}
        className="w-full text-left rounded-lg px-1.5 py-1.5 -mx-1.5 hover:bg-white/70 transition-colors min-h-[44px]" data-testid="help-request-banner-item">
        <span className="block text-xs font-bold text-torre-slate line-clamp-2 break-words">{request.titulo}</span>
        {meta && <span className="block text-[11px] text-amber-700 line-clamp-2">{meta}</span>}
      </button>
    </div>
  );
}
